import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  Shield, 
  Users, 
  Settings, 
  Save, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  User,
  Lock,
  Unlock,
  Eye,
  Edit,
  Trash2,
  Plus,
  X
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { usersService } from '@/services/users'
import { brandsService } from '@/services/brands'
import toast from 'react-hot-toast'

const DeveloperPermissions = () => {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  
  const [activeTab, setActiveTab] = useState('role-matrix')
  const [selectedUser, setSelectedUser] = useState(null)
  const [permissionChanges, setPermissionChanges] = useState({})
  const [editingUser, setEditingUser] = useState(null)
  const [showUserEditModal, setShowUserEditModal] = useState(false)

  // Fetch all users for permission management
  const {
    data: users,
    isLoading: usersLoading,
    error: usersError
  } = useQuery(
    ['users', 'all'],
    () => usersService.getAll({ page: 1, size: 1000 }),
    {
      onError: (error) => {
        console.error('Error loading users:', error)
        toast.error('Failed to load users')
      }
    }
  )

  // Fetch all brands for permission management
  const {
    data: brands,
    isLoading: brandsLoading
  } = useQuery(
    ['brands', 'all'],
    () => brandsService.getAll({ page: 1, size: 1000 }),
    {
      onError: (error) => {
        console.error('Error loading brands:', error)
        toast.error('Failed to load brands')
      }
    }
  )

  // Update user role mutation
  const updateUserRoleMutation = useMutation(
    ({ userId, roleData }) => usersService.updateRole(userId, roleData),
    {
      onSuccess: () => {
        toast.success('User role updated successfully')
        queryClient.invalidateQueries(['users'])
        setShowUserEditModal(false)
        setEditingUser(null)
      },
      onError: (error) => {
        console.error('Error updating user role:', error)
        toast.error('Failed to update user role')
      }
    }
  )

  // Role permission matrix configuration
  const rolePermissionMatrix = {
    admin: {
      name: 'Administrator',
      description: 'Full system access with all permissions',
      permissions: {
        users: ['create', 'read', 'update', 'delete'],
        brands: ['create', 'read', 'update', 'delete'],
        data_inputs: ['create', 'read', 'update', 'delete'],
        records: ['create', 'read', 'update', 'delete'],
        system: ['admin']
      },
      requires_brand: false,
      color: 'red'
    },
    manager: {
      name: 'Manager',
      description: 'Full data management within assigned brands',
      permissions: {
        users: ['create', 'read', 'update', 'delete'],
        brands: ['read', 'update'],
        data_inputs: ['create', 'read', 'update', 'delete'],
        records: ['create', 'read', 'update', 'delete']
      },
      requires_brand: true,
      color: 'blue'
    },
    user: {
      name: 'User',
      description: 'Record management within assigned brands',
      permissions: {
        brands: ['read'],
        data_inputs: ['read'],
        records: ['create', 'read', 'update', 'delete']
      },
      requires_brand: true,
      data_input_only: true,
      color: 'green'
    }
  }

  const permissionLabels = {
    create: 'Create',
    read: 'Read',
    update: 'Update',
    delete: 'Delete',
    admin: 'Admin'
  }

  const resourceLabels = {
    users: 'Users',
    brands: 'Brands',
    data_inputs: 'Data Inputs',
    records: 'Records',
    system: 'System'
  }

  const tabs = [
    {
      id: 'role-matrix',
      name: 'Role Permission Matrix',
      icon: Shield,
      description: 'View and understand role-based permissions'
    },
    {
      id: 'user-permissions',
      name: 'User Permissions',
      icon: Users,
      description: 'Manage individual user permissions and brand access'
    },
    {
      id: 'system-settings',
      name: 'System Settings',
      icon: Settings,
      description: 'Advanced permission system configuration'
    }
  ]

  const getRoleColor = (role) => {
    const colors = {
      red: 'bg-red-100 text-red-800 border-red-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200'
    }
    return colors[rolePermissionMatrix[role]?.color] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const renderRoleMatrix = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Role Permission Matrix</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>This matrix shows the current permission structure for each role. These permissions are defined in the backend and cannot be modified from this interface.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        {Object.entries(rolePermissionMatrix).map(([roleKey, roleData]) => (
          <div key={roleKey} className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(roleKey)}`}>
                    {roleData.name}
                  </span>
                  <h3 className="text-lg font-medium text-gray-900">{roleData.name} Role</h3>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  {roleData.requires_brand && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-yellow-100 text-yellow-800 text-xs">
                      <Lock className="h-3 w-3 mr-1" />
                      Brand Required
                    </span>
                  )}
                  {roleData.data_input_only && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-purple-100 text-purple-800 text-xs">
                      Data Input Only
                    </span>
                  )}
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600">{roleData.description}</p>
            </div>
            <div className="p-6">
              <div className="grid gap-4">
                {Object.entries(roleData.permissions).map(([resource, permissions]) => (
                  <div key={resource} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-900 min-w-[100px]">
                        {resourceLabels[resource]}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {permissions.map((permission) => (
                        <span
                          key={permission}
                          className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 text-green-800 text-xs font-medium"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {permissionLabels[permission]}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderUserPermissions = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">User Permission Management</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Manage individual user roles and brand access. Changes to user roles and brand associations will take effect immediately.</p>
            </div>
          </div>
        </div>
      </div>

      {usersLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading users...</span>
        </div>
      ) : usersError ? (
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Users</h3>
          <p className="text-gray-500">Failed to load user data. Please try again.</p>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">User List</h3>
            <p className="mt-1 text-sm text-gray-600">
              Click on a user to view and manage their permissions
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {users?.users?.map((user) => (
              <div
                key={user.id}
                className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedUser?.id === user.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
                onClick={() => setSelectedUser(user)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-500" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_superuser 
                        ? 'bg-red-100 text-red-800' 
                        : user.role === 'manager'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {user.is_superuser ? 'Admin' : user.role || 'User'}
                    </span>
                    {!user.active && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-800 text-xs">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditingUser(user)
                        setShowUserEditModal(true)
                      }}
                      className="text-blue-600 hover:text-blue-900 text-sm"
                    >
                      Edit Role
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              User Details: {selectedUser.first_name} {selectedUser.last_name}
            </h3>
            <p className="mt-1 text-sm text-gray-600">{selectedUser.email}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-xs text-gray-500">Username</dt>
                    <dd className="text-sm text-gray-900">{selectedUser.username}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Role</dt>
                    <dd className="text-sm text-gray-900">
                      {selectedUser.is_superuser ? 'Administrator' : selectedUser.role || 'User'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Status</dt>
                    <dd className="text-sm text-gray-900">
                      {selectedUser.active ? 'Active' : 'Inactive'}
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Brand Access</h4>
                {selectedUser.brand_access && selectedUser.brand_access.length > 0 ? (
                  <div className="space-y-2">
                    {selectedUser.brand_access.map((access) => (
                      <div key={access.brand_id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {access.brand_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            Role: {access.brand_role}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {access.can_view && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-xs">
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </span>
                          )}
                          {access.can_create && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 text-green-800 text-xs">
                              <Plus className="h-3 w-3 mr-1" />
                              Create
                            </span>
                          )}
                          {access.can_edit && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-yellow-100 text-yellow-800 text-xs">
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </span>
                          )}
                          {access.can_delete && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-red-100 text-red-800 text-xs">
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No brand access configured</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  const renderSystemSettings = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">System Configuration</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>Advanced permission system settings. Changes here affect the entire application. Use with caution.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Permission System Status</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="mt-2 text-sm font-medium text-gray-900">Role System</h4>
              <p className="text-sm text-gray-500">Active</p>
            </div>
            <div className="text-center">
              <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="mt-2 text-sm font-medium text-gray-900">Brand Access</h4>
              <p className="text-sm text-gray-500">Active</p>
            </div>
            <div className="text-center">
              <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="mt-2 text-sm font-medium text-gray-900">API Protection</h4>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Developer Permissions</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage system permissions, roles, and user access controls
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <Shield className="h-3 w-3 mr-1" />
            Admin Only
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'role-matrix' && renderRoleMatrix()}
        {activeTab === 'user-permissions' && renderUserPermissions()}
        {activeTab === 'system-settings' && renderSystemSettings()}
      </div>

      {/* User Edit Modal */}
      {showUserEditModal && editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit User Role
                </h3>
                <button
                  onClick={() => {
                    setShowUserEditModal(false)
                    setEditingUser(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User: {editingUser.first_name} {editingUser.last_name}
                  </label>
                  <p className="text-sm text-gray-500">{editingUser.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={editingUser.is_superuser ? 'admin' : editingUser.role || 'user'}
                    onChange={(e) => {
                      const newRole = e.target.value
                      setEditingUser({
                        ...editingUser,
                        role: newRole === 'admin' ? null : newRole,
                        is_superuser: newRole === 'admin'
                      })
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex">
                    <AlertCircle className="h-4 w-4 text-yellow-400 mt-0.5" />
                    <div className="ml-2">
                      <p className="text-xs text-yellow-700">
                        Role changes take effect immediately. Brand access may need to be configured separately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowUserEditModal(false)
                    setEditingUser(null)
                  }}
                  className="btn-outline btn-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    updateUserRoleMutation.mutate({
                      userId: editingUser.id,
                      roleData: {
                        role: editingUser.role,
                        is_superuser: editingUser.is_superuser
                      }
                    })
                  }}
                  disabled={updateUserRoleMutation.isLoading}
                  className="btn-primary btn-sm flex items-center"
                >
                  {updateUserRoleMutation.isLoading ? (
                    <>
                      <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-3 w-3 mr-1" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeveloperPermissions
